/**
 * AdminLayout - Main layout wrapper for admin pages
 *
 * Uses unified EntityContext for entity data, features, and theming.
 * Handles SSO domain restrictions and OAuth error handling.
 */

import React, { useState, useEffect } from 'react';
import {
  Box,
  Drawer,
  AppBar,
  Toolbar,
  List,
  Typography,
  IconButton,
  ListItem,
  ListItemIcon,
  ListItemText,
  Avatar,
  Menu,
  MenuItem,
  useTheme,
  alpha,
  Collapse,
  CircularProgress,
  Select,
  SelectChangeEvent
} from '@mui/material';
import {
  Menu as MenuIcon,
  Dashboard,
  Work,
  People,
  Analytics,
  Phone,
  Settings,
  Logout,
  AccountCircle,
  Shield as ShieldIcon,
  Business as BusinessIcon,
  SupervisorAccount as SupervisorAccountIcon,
  Add as AddIcon,
  CloudUpload as CloudUploadIcon,
  Assessment as AssessmentIcon,
  ExpandLess,
  ExpandMore
} from '@mui/icons-material';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useEntity } from '../../context/EntityContext';
import { signOut } from '../../services/authService';
import { NotificationBell } from '../NotificationBell';
import { FeatureFlags } from '../../types';

// Allowed email domains for SSO login
const ALLOWED_SSO_DOMAINS = ['gargash.ae'];

const drawerWidth = 240;

interface AdminLayoutProps {
  children: React.ReactNode;
}

interface NavItem {
  text: string;
  icon: React.ReactNode;
  path?: string;
  feature?: keyof FeatureFlags;
  indent?: boolean;
  children?: NavItem[];
}

export const AdminLayout: React.FC<AdminLayoutProps> = ({ children }) => {
  // UI State
  const [mobileOpen, setMobileOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [openMenus, setOpenMenus] = useState<{ [key: string]: boolean }>({
    Jobs: false,
    Applications: false
  });

  // Entity Context - Single source of truth for entity data
  const {
    entity,
    features,
    theme: entityTheme,
    isLoading,
    currentUser,
    isSuperAdmin,
    userEntities,
    hasMultipleEntities,
    setUserEntity
  } = useEntity();

  // Entity switcher state
  const [switchingEntity, setSwitchingEntity] = useState(false);

  const handleEntitySwitch = async (event: SelectChangeEvent<string>) => {
    const newEntityId = event.target.value;
    if (newEntityId && newEntityId !== entity?.id) {
      setSwitchingEntity(true);
      try {
        await setUserEntity(newEntityId);
      } finally {
        setSwitchingEntity(false);
      }
    }
  };

  // Router
  const navigate = useNavigate();
  const location = useLocation();
  const muiTheme = useTheme();

  // ============================================================================
  // OAuth Error Handling & SSO Domain Check
  // ============================================================================

  useEffect(() => {
    // Check for OAuth errors in URL (e.g., access_denied from Microsoft)
    const urlParams = new URLSearchParams(window.location.search);
    const hashParams = new URLSearchParams(window.location.hash.replace('#', '?'));
    const oauthError = urlParams.get('error') || hashParams.get('error');

    if (oauthError) {
      console.warn(`[Auth] OAuth error detected: ${oauthError}`);
      signOut().then(() => {
        const errorCode = oauthError === 'access_denied' ? 'access_denied' : 'auth_error';
        navigate(`/admin/login?error=${errorCode}`, { replace: true });
      });
      return;
    }

    // Check SSO domain restriction when user is loaded
    if (currentUser) {
      const userEmail = currentUser.email || '';
      const emailDomain = userEmail.split('@')[1]?.toLowerCase();
      const appMetadata = (currentUser as any).app_metadata || {};
      const isSSOUser = appMetadata.provider === 'azure' ||
                        appMetadata.providers?.includes('azure');

      if (isSSOUser && emailDomain && !ALLOWED_SSO_DOMAINS.includes(emailDomain)) {
        console.warn(`[Auth] SSO login blocked for domain: ${emailDomain}`);
        signOut().then(() => {
          navigate('/admin/login?error=domain_restricted');
        });
      }
    }
  }, [currentUser, navigate]);

  // ============================================================================
  // Feature Flag Check - Uses EntityContext features
  // ============================================================================

  const isFeatureEnabled = (feature: keyof FeatureFlags): boolean => {
    return features[feature] === true;
  };

  // ============================================================================
  // Navigation Items
  // ============================================================================

  const superAdminNavItems: NavItem[] = [
    { text: 'Entities', icon: <BusinessIcon />, path: '/admin/super-admin?tab=0' },
    { text: 'Users', icon: <SupervisorAccountIcon />, path: '/admin/super-admin?tab=1' },
    { text: 'Features', icon: <Settings />, path: '/admin/super-admin?tab=2' },
    { text: 'Entity Analytics', icon: <Analytics />, path: '/admin/entity-analytics' }
  ];

  const regularNavItems: NavItem[] = [
    { text: 'Dashboard', icon: <Dashboard />, path: '/admin/dashboard' },
    {
      text: 'Job Management',
      icon: <Work />,
      children: [
        { text: 'Jobs', icon: <Work />, path: '/admin/jobs' },
        { text: 'Add New Job', icon: <AddIcon />, path: '/admin/jobs/post' }
      ]
    },
    {
      text: 'Applications',
      icon: <People />,
      path: '/admin/applications',
      children: [
        { text: 'View Applications', icon: <People />, path: '/admin/applications' },
        { text: 'Bulk Resume Upload', icon: <CloudUploadIcon />, path: '/admin/bulk-matching', feature: 'aiScreening' },
        { text: 'Call Logs', icon: <AssessmentIcon />, path: '/admin/call-logs', feature: 'callInitiation' },
        { text: 'Call Screening', icon: <Phone />, path: '/admin/calls', feature: 'callInitiation' }
      ]
    },
    { text: 'Analytics', icon: <Analytics />, path: '/admin/analytics', feature: 'analytics' },
    { text: 'Settings', icon: <Settings />, path: '/admin/settings' }
  ];

  const navItems = isSuperAdmin ? superAdminNavItems : regularNavItems;

  // ============================================================================
  // Event Handlers
  // ============================================================================

  const handleDrawerToggle = () => setMobileOpen(!mobileOpen);

  const handleMenuToggle = (menuName: string) => {
    setOpenMenus(prev => ({ ...prev, [menuName]: !prev[menuName] }));
  };

  const handleProfileMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleProfileMenuClose = () => setAnchorEl(null);

  const handleProfileClick = () => {
    navigate('/admin/profile');
    handleProfileMenuClose();
  };

  const handleLogout = async () => {
    await signOut();
    navigate('/admin/login');
    handleProfileMenuClose();
  };

  // ============================================================================
  // Display Values - From EntityContext
  // ============================================================================

  const displayLogoUrl = entityTheme.logoUrl;
  const displayName = entity?.name || 'HR Portal';

  // ============================================================================
  // Loading State - Only show full-page loader on initial load (no entity yet)
  // Once entity is loaded, keep children mounted to preserve form state
  // ============================================================================

  const showFullPageLoader = isLoading && !entity && !currentUser;

  // ============================================================================
  // Drawer Content
  // ============================================================================

  const drawer = (
    <Box sx={{
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
      bgcolor: '#f8f9fa !important',
      borderRight: `1px solid #e0e0e0`
    }}>
      <Toolbar sx={{
        minHeight: 70,
        borderBottom: `1px solid #e0e0e0`,
        bgcolor: '#ffffff'
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', gap: 1.5 }}>
          {displayLogoUrl && (
            <Box sx={{
              width: 40,
              height: 40,
              borderRadius: 2,
              overflow: 'hidden',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: '#f5f5f5',
              border: `1px solid #e0e0e0`
            }}>
              <img
                src={displayLogoUrl}
                alt={`${displayName} logo`}
                style={{ height: 30, width: 'auto' }}
              />
            </Box>
          )}
          <Box>
            <Typography
              variant="h6"
              noWrap
              component="div"
              sx={{
                fontSize: '1.1rem',
                fontWeight: 700,
                color: 'text.primary',
                lineHeight: 1.2
              }}
            >
              {displayName}
            </Typography>
            <Typography
              variant="caption"
              sx={{ color: 'text.secondary', fontSize: '0.7rem' }}
            >
              Management
            </Typography>
          </Box>
        </Box>
      </Toolbar>

      <Box sx={{ py: 2, flexGrow: 1, overflow: 'auto' }}>
        <List sx={{ px: 2 }}>
          {navItems.map((item) => {
            if (item.feature && !isFeatureEnabled(item.feature)) {
              return null;
            }

            const hasChildren = item.children && item.children.length > 0;
            const filteredChildren = item.children?.filter(
              child => !child.feature || isFeatureEnabled(child.feature)
            ) || [];

            const isActive = item.path
              ? (isSuperAdmin
                  ? location.pathname + location.search === item.path
                  : location.pathname === item.path)
              : false;

            const isChildActive = filteredChildren.some(
              child => location.pathname === child.path
            );

            return (
              <React.Fragment key={item.text}>
                <ListItem
                  component={item.path && !hasChildren ? Link : 'div'}
                  {...(item.path && !hasChildren ? { to: item.path } : {})}
                  onClick={(e: React.MouseEvent) => {
                    if (hasChildren) {
                      e.preventDefault();
                      handleMenuToggle(item.text);
                    } else if (item.path) {
                      navigate(item.path);
                    }
                  }}
                  sx={{
                    color: 'inherit',
                    textDecoration: 'none',
                    borderRadius: 2,
                    mb: 0.5,
                    transition: 'all 0.2s ease',
                    cursor: 'pointer',
                    backgroundColor: (isActive || isChildActive) ? '#ffffff' : 'transparent',
                    border: (isActive || isChildActive) ? '1px solid #e0e0e0' : '1px solid transparent',
                    boxShadow: (isActive || isChildActive) ? '0 1px 3px rgba(0,0,0,0.08)' : 'none',
                    '&:hover': {
                      backgroundColor: '#ffffff',
                      border: '1px solid #e0e0e0',
                      boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
                    }
                  }}
                >
                  <ListItemIcon sx={{
                    color: (isActive || isChildActive) ? 'primary.main' : 'text.secondary',
                    minWidth: 40,
                    transition: 'color 0.3s',
                  }}>
                    {item.icon}
                  </ListItemIcon>
                  <ListItemText
                    primary={item.text}
                    primaryTypographyProps={{
                      fontWeight: (isActive || isChildActive) ? 600 : 500,
                      fontSize: '0.95rem',
                      color: (isActive || isChildActive) ? 'primary.main' : 'text.primary',
                    }}
                  />
                  {hasChildren && (
                    openMenus[item.text] ? <ExpandLess /> : <ExpandMore />
                  )}
                </ListItem>

                {hasChildren && filteredChildren.length > 0 && (
                  <Collapse in={openMenus[item.text]} timeout="auto" unmountOnExit>
                    <List component="div" disablePadding>
                      {filteredChildren.map((child) => {
                        const isChildItemActive = location.pathname === child.path;

                        return (
                          <ListItem
                            key={child.text}
                            component={Link}
                            to={child.path || ''}
                            sx={{
                              color: 'inherit',
                              textDecoration: 'none',
                              borderRadius: 2,
                              mb: 0.5,
                              ml: 3,
                              transition: 'all 0.2s ease',
                              backgroundColor: isChildItemActive ? '#ffffff' : 'transparent',
                              border: isChildItemActive ? '1px solid #e0e0e0' : '1px solid transparent',
                              boxShadow: isChildItemActive ? '0 1px 3px rgba(0,0,0,0.08)' : 'none',
                              '&:hover': {
                                backgroundColor: '#ffffff',
                                border: '1px solid #e0e0e0',
                                boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
                              }
                            }}
                          >
                            <ListItemIcon sx={{
                              color: isChildItemActive ? 'primary.main' : 'text.secondary',
                              minWidth: 36,
                              transition: 'color 0.3s',
                            }}>
                              {child.icon}
                            </ListItemIcon>
                            <ListItemText
                              primary={child.text}
                              primaryTypographyProps={{
                                fontWeight: isChildItemActive ? 600 : 500,
                                fontSize: '0.875rem',
                                color: isChildItemActive ? 'primary.main' : 'text.primary',
                              }}
                            />
                          </ListItem>
                        );
                      })}
                    </List>
                  </Collapse>
                )}
              </React.Fragment>
            );
          })}
        </List>
      </Box>
    </Box>
  );

  // ============================================================================
  // Main Render
  // ============================================================================

  // Only show full-page loader on initial load (before any data is loaded)
  // This prevents unmounting children during background re-validation
  if (showFullPageLoader) {
    return (
      <Box sx={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '100vh',
        bgcolor: '#f5f5f5'
      }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex' }}>
      <AppBar
        position="fixed"
        elevation={0}
        sx={{
          width: { sm: `calc(100% - ${drawerWidth}px)` },
          ml: { sm: `${drawerWidth}px` },
          bgcolor: '#fff',
          color: '#333',
          borderBottom: `1px solid #e0e0e0`,
          boxShadow: 'none'
        }}
      >
        <Toolbar sx={{ minHeight: 70 }}>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{
              mr: 2,
              display: { sm: 'none' },
              '&:hover': { backgroundColor: '#f5f5f5' }
            }}
          >
            <MenuIcon />
          </IconButton>

          <Box sx={{ flexGrow: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography
                variant="h5"
                noWrap
                component="div"
                sx={{ fontWeight: 600, letterSpacing: '-0.5px', color: '#333' }}
              >
                HR Portal
              </Typography>
              {isSuperAdmin && (
                <Box sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.5,
                  bgcolor: '#ff5252',
                  px: 1.5,
                  py: 0.5,
                  borderRadius: 1
                }}>
                  <ShieldIcon sx={{ fontSize: 14, color: '#fff' }} />
                  <Typography
                    variant="caption"
                    sx={{
                      color: '#fff',
                      fontSize: '0.7rem',
                      fontWeight: 600,
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px'
                    }}
                  >
                    Super Admin
                  </Typography>
                </Box>
              )}
            </Box>
            <Typography
              variant="caption"
              sx={{ color: '#666', fontSize: '0.75rem', fontWeight: 400 }}
            >
              {isSuperAdmin ? 'Platform Management' : 'Admin Dashboard'}
            </Typography>
          </Box>

          {hasMultipleEntities && (
            <Select
              size="small"
              value={entity?.id || ''}
              onChange={handleEntitySwitch}
              disabled={switchingEntity || isLoading}
              sx={{
                minWidth: 180,
                mr: 2,
                bgcolor: '#f5f5f5',
                borderRadius: 1,
                '& .MuiSelect-select': {
                  py: 0.75,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                },
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: '#e0e0e0',
                },
              }}
              startAdornment={
                <BusinessIcon sx={{ fontSize: 18, color: 'text.secondary', mr: 0.5 }} />
              }
            >
              {userEntities.map((ue) => (
                <MenuItem key={ue.id} value={ue.id}>
                  {ue.name}
                </MenuItem>
              ))}
            </Select>
          )}

          <NotificationBell />

          <IconButton
            size="large"
            edge="end"
            aria-label="account of current user"
            onClick={handleProfileMenuOpen}
            sx={{
              ml: 1,
              color: '#333',
              backgroundColor: '#f5f5f5',
              '&:hover': { backgroundColor: '#e0e0e0' }
            }}
          >
            <AccountCircle />
          </IconButton>

          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleProfileMenuClose}
            PaperProps={{
              sx: {
                mt: 1.5,
                minWidth: 200,
                borderRadius: 2,
                boxShadow: `0 8px 32px ${alpha(muiTheme.palette.primary.main, 0.15)}`,
                border: `1px solid ${alpha(muiTheme.palette.divider, 0.1)}`
              }
            }}
          >
            <MenuItem
              onClick={handleProfileClick}
              sx={{
                py: 1.5,
                borderRadius: 1,
                mx: 1,
                my: 0.5,
                '&:hover': { backgroundColor: alpha(muiTheme.palette.primary.main, 0.08) }
              }}
            >
              <Avatar sx={{
                mr: 2,
                width: 32,
                height: 32,
                bgcolor: alpha(muiTheme.palette.primary.main, 0.1),
                color: 'primary.main'
              }} />
              <Box>
                <Typography variant="body2" fontWeight={600}>Profile</Typography>
                <Typography variant="caption" color="text.secondary">View your profile</Typography>
              </Box>
            </MenuItem>
            <MenuItem
              onClick={handleLogout}
              sx={{
                py: 1.5,
                borderRadius: 1,
                mx: 1,
                my: 0.5,
                color: 'error.main',
                '&:hover': { backgroundColor: alpha(muiTheme.palette.error.main, 0.08) }
              }}
            >
              <Logout sx={{ mr: 2 }} />
              <Box>
                <Typography variant="body2" fontWeight={600}>Logout</Typography>
                <Typography variant="caption" color="text.secondary">Sign out of account</Typography>
              </Box>
            </MenuItem>
          </Menu>
        </Toolbar>
      </AppBar>

      <Box component="nav" sx={{ width: { sm: drawerWidth }, flexShrink: { sm: 0 } }}>
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{ keepMounted: true }}
          sx={{
            display: { xs: 'block', sm: 'none' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: drawerWidth,
              bgcolor: '#f8f9fa !important',
              borderRight: '1px solid #e0e0e0',
              borderRadius: 0
            },
          }}
        >
          {drawer}
        </Drawer>
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', sm: 'block' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: drawerWidth,
              bgcolor: '#f8f9fa !important',
              borderRight: '1px solid #e0e0e0',
              borderRadius: 0
            },
          }}
          open
        >
          {drawer}
        </Drawer>
      </Box>

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 3,
          width: { sm: `calc(100% - ${drawerWidth}px)` },
          mt: '70px',
          minHeight: 'calc(100vh - 70px)',
          backgroundColor: alpha(muiTheme.palette.background.default, 0.5)
        }}
      >
        {children}
      </Box>
    </Box>
  );
};
